import { useState, useRef, useCallback, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CalendarDays, Plus, Trash2, Download, Loader2, Star, X, ImageIcon, Sparkles, Search, Eye, ChevronLeft, ChevronRight, Lock, Filter, Pencil, RotateCcw, Copy, AlertTriangle, CheckCircle2, Ban, ClipboardList, Target, FileText, Users } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PROFESSIONAL_ROLES, functionOptions, functionToFr, functionLabel } from "../lib/constants";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { AffichePreview } from "./AffichePreview";
import PlanningRecoPanel from "../components/PlanningRecoPanel";
import ActivityFicheDialog from "../components/ActivityFicheDialog";
import BilanDialog from "../components/BilanDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const DAYS_NL = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const DAYS_EN = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const dayName = (i, lang) => (lang === "nl" ? DAYS_NL : lang === "en" ? DAYS_EN : DAYS)[i];
const THEMES = ["rose", "bleu", "vert", "orange", "violet", "beige", "turquoise", "corail", "lavande", "menthe", "jaune", "rouge", "indigo", "cyan", "lime", "brun", "gris", "bordeaux", "sauge", "ciel"];
const THEME_HEX = { rose: "#E75480", bleu: "#1E88E5", vert: "#43A047", orange: "#FB8C00", violet: "#8E24AA", beige: "#C8A96A", turquoise: "#00ACC1", corail: "#FF7043", lavande: "#9575CD", menthe: "#26A69A", jaune: "#FDD835", rouge: "#E53935", indigo: "#3949AB", cyan: "#00838F", lime: "#7CB342", brun: "#8D6E63", gris: "#607D8B", bordeaux: "#880E4F", sauge: "#7A9E7E", ciel: "#4FC3F7" };
const THEME_NAMES = {
  nl: { rose: "Roze", bleu: "Blauw", vert: "Groen", orange: "Oranje", violet: "Paars", beige: "Beige", turquoise: "Turkoois", corail: "Koraal", lavande: "Lavendel", menthe: "Munt", jaune: "Geel", rouge: "Rood", indigo: "Indigo", cyan: "Cyaan", lime: "Limoen", brun: "Bruin", gris: "Grijs", bordeaux: "Bordeaux", sauge: "Salie", ciel: "Hemelsblauw" },
  en: { rose: "Pink", bleu: "Blue", vert: "Green", orange: "Orange", violet: "Purple", beige: "Beige", turquoise: "Turquoise", corail: "Coral", lavande: "Lavender", menthe: "Mint", jaune: "Yellow", rouge: "Red", indigo: "Indigo", cyan: "Cyan", lime: "Lime", brun: "Brown", gris: "Grey", bordeaux: "Burgundy", sauge: "Sage", ciel: "Sky blue" },
};
const themeLabel = (th, lang) => (THEME_NAMES[lang] && THEME_NAMES[lang][th]) || th;
const FOOTER_DEFAULTS = { fr: "Bonne semaine !", nl: "Fijne week !", en: "Have a nice week!" };
const isDefaultFooter = (f) => Object.values(FOOTER_DEFAULTS).includes((f || "").trim());

const empty = { name: "", name_nl: "", name_en: "", catalog_id: "", start: "", end: "", place: "", animators: "", function: "", day: 0, moment: "morning", image_id: "", image_url: "", objectives: [], dedicated: false };
const imgSrc = (url) => (url ? (url.startsWith("http") ? url : BACKEND + url) : "");

const FR_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const NL_MONTHS = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
const EN_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
// À partir d'une date choisie, cale au lundi de la semaine et construit la période "Du lundi ... au dimanche ...".
function weekInfo(dateStr, lang = "fr") {
  if (!dateStr) return { monday: "", period: "" };
  const [y, m, d] = dateStr.split("-").map(Number);
  const start = new Date(y, m - 1, d);
  const dow = start.getDay(); // 0 = dimanche
  start.setDate(start.getDate() + (dow === 0 ? -6 : 1 - dow));
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const pad = (n) => String(n).padStart(2, "0");
  const monday = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
  const months = lang === "nl" ? NL_MONTHS : lang === "en" ? EN_MONTHS : FR_MONTHS;
  const sd = pad(start.getDate()), ed = pad(end.getDate());
  const sm = months[start.getMonth()], em = months[end.getMonth()];
  const sy = start.getFullYear(), ey = end.getFullYear();
  let period;
  if (lang === "nl") {
    period = sy === ey ? `Van maandag ${sd} ${sm} t/m zondag ${ed} ${em} ${ey}` : `Van maandag ${sd} ${sm} ${sy} t/m zondag ${ed} ${em} ${ey}`;
  } else if (lang === "en") {
    period = sy === ey ? `From Monday ${sd} ${sm} to Sunday ${ed} ${em} ${ey}` : `From Monday ${sd} ${sm} ${sy} to Sunday ${ed} ${em} ${ey}`;
  } else {
    period = sy === ey ? `Du lundi ${sd} ${sm} au dimanche ${ed} ${em} ${ey}` : `Du lundi ${sd} ${sm} ${sy} au dimanche ${ed} ${em} ${ey}`;
  }
  return { monday, period };
}
const normKey = (s) => (s || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const pad = (n) => String(n).padStart(2, "0");
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };

const ACTIVITIES = ["Atelier bien-être", "Atelier culinaire", "Atelier équilibre", "Atelier mémoire", "Bingo", "Chorale", "Club de lecture", "Coiffure", "Cuisine", "Danse", "Décoration", "Écoute musicale", "Gym douce", "Jardinage", "Jeu de mots", "Jeux de société", "Le Petit Bac", "Loto", "Manucure", "Marche", "Massage / Toucher massage", "Méditation", "Messe", "Musique et mouvement", "Pâtisserie", "Peinture", "Pétanque", "Projection film", "Quiz", "Rédaction carte de vœux", "Relaxation", "Réunion des seniors", "Revue de presse", "Rituel d'histoire", "Saint Nicolas", "Saint Valentin", "Scrabble", "Séance de méditation", "Serenity Magazine", "Shooting photo", "Snoezelen", "Sophrologie", "Sortie au marché", "Sortie au restaurant", "Sortie course", "Sortie culturelle", "Sortie-promenade", "Speed dating", "Stimulation cognitive", "Suivi logopédique", "Tournage de vidéo", "Tricot / Couture", "Venez tester votre QI", "Visite d'élèves", "Yoga"];

export default function PlanningActivite() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isPro = user && PROFESSIONAL_ROLES.includes(user.role);
  const isReferentDementia = (() => { const v = normKey((user?.function || "") + " " + (user?.role || "")); return v.includes("referent") && v.includes("demence"); })();
  const [planView, setPlanView] = useState("commun"); // "commun" | "referent"
  const [canDedicated, setCanDedicated] = useState(false); // accès délégué au planning dédié démence
  const [accessOpen, setAccessOpen] = useState(false);
  const [delegates, setDelegates] = useState(null);
  const isAdmin = user && user.role === "admin";
  const [meta, setMeta] = useState(() => { const w = weekInfo(todayISO(), "fr"); return { group: "Novadia", residence: "", period: w.period, week_start: w.monday, footer: "Bonne semaine !", lang: "fr", theme: "bleu" }; });
  const [acts, setActs] = useState([]);
  const [form, setForm] = useState({ ...empty });
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({ func: "", prof: "", q: "" });
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [photoStyle, setPhotoStyle] = useState("photo"); // "photo" | "illustration"
  const [loading, setLoading] = useState(false);
  const [pageFormat, setPageFormat] = useState("A4");
  const [lib, setLib] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [otypes, setOtypes] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [defaultPanel, setDefaultPanel] = useState(null);   // {fromCatalog, catalogId?}
  const [showPreview, setShowPreview] = useState(false);
  const [previewLangs, setPreviewLangs] = useState(["fr"]);
  // Copier la semaine
  const canCopyAll = user && ["admin", "directeur", "directeur_adjoint", "responsable_unite"].includes(user.role);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTarget, setCopyTarget] = useState("");
  const [copyScope, setCopyScope] = useState("mine");
  const [copyPreview, setCopyPreview] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyPlan, setCopyPlan] = useState("commun");
  const togglePreviewLang = (lng) => setPreviewLangs((ls) => ls.includes(lng) ? (ls.length > 1 ? ls.filter((l) => l !== lng) : ls) : [...ls, lng]);
  const metaForLang = (lng) => ({ ...meta, lang: lng, period: meta.week_start ? weekInfo(meta.week_start, lng).period : meta.period, footer: isDefaultFooter(meta.footer) ? (FOOTER_DEFAULTS[lng] || meta.footer) : meta.footer });
  const timer = useRef(null);

  // Ouverture directe sur une semaine précise via ?week=YYYY-MM-DD (depuis une notification)
  useEffect(() => {
    const wk = new URLSearchParams(window.location.search).get("week");
    if (wk && /^\d{4}-\d{2}-\d{2}$/.test(wk)) {
      const w = weekInfo(wk, i18n.language || "fr");
      setMeta((m) => ({ ...m, week_start: w.monday, period: w.period }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Catalogue central (activités FR/NL + image par défaut)
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/planning/catalog"); setCatalog(data.activities || []); }
      catch { /* silencieux */ }
      try { const { data } = await api.get("/pp/objective-types"); setOtypes(data.objective_types || []); }
      catch { /* silencieux */ }
    })();
  }, []);

  // Planning collaboratif : charge toutes les activités de la semaine (tous les métiers de la résidence)
  const loadEntries = useCallback(async (ws) => {
    if (!ws) return;
    setLoadingEntries(true);
    try {
      const { data } = await api.get("/planning/entries", { params: { week_start: ws } });
      setActs(data.entries || []);
      setCanDedicated(!!data.can_dedicated);
      if (data.maison && data.maison !== "Toutes les maisons") setMeta((m) => ({ ...m, residence: m.residence || data.maison }));
    } catch { /* silencieux */ }
    setLoadingEntries(false);
  }, []);
  useEffect(() => { loadEntries(meta.week_start); }, [meta.week_start, loadEntries]);
  // La langue de l'affiche + de la grille suit la langue de l'application (sélecteur du top-bar)
  useEffect(() => { const lng = (i18n.language || "fr").slice(0, 2); setMeta((m) => ({ ...m, lang: lng, period: m.week_start ? weekInfo(m.week_start, lng).period : m.period, footer: isDefaultFooter(m.footer) ? (FOOTER_DEFAULTS[lng] || m.footer) : m.footer })); }, [i18n.language]);
  const shiftWeek = (days) => { const [y, mo, d] = meta.week_start.split("-").map(Number); const dt = new Date(y, mo - 1, d); dt.setDate(dt.getDate() + days); const p = (n) => String(n).padStart(2, "0"); const w = weekInfo(`${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`, meta.lang); setMeta((m) => ({ ...m, week_start: w.monday, period: w.period })); };

  // ── Accès délégués au planning dédié « Référent démence » ──
  const openAccess = async () => {
    setAccessOpen(true); setDelegates(null);
    try { const { data } = await api.get("/planning/dementia/delegates"); setDelegates(data.delegates || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setDelegates([]); }
  };
  const toggleDelegate = async (username, granted) => {
    setDelegates((ds) => (ds || []).map((d) => (d.username === username ? { ...d, granted } : d)));
    try {
      await api.post("/planning/dementia/delegates", { username, granted });
      toast.success(granted ? t("planning.accessGranted") : t("planning.accessRevoked"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setDelegates((ds) => (ds || []).map((d) => (d.username === username ? { ...d, granted: !granted } : d)));
    }
  };

  // ── Copier la semaine ──
  const openCopy = () => {
    const [y, mo, d] = meta.week_start.split("-").map(Number);
    const dt = new Date(y, mo - 1, d); dt.setDate(dt.getDate() + 7);
    setCopyTarget(weekInfo(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`, meta.lang).monday);
    setCopyScope("mine"); setCopyPreview(null); setCopyPlan(isReferentDementia ? planView : "commun"); setCopyOpen(true);
  };
  const setCopyTargetDate = (val) => { if (!val) return; setCopyTarget(weekInfo(val, meta.lang).monday); setCopyPreview(null); };
  const shiftCopyTarget = (days) => { if (!copyTarget) return; const [y, mo, d] = copyTarget.split("-").map(Number); const dt = new Date(y, mo - 1, d); dt.setDate(dt.getDate() + days); setCopyTarget(weekInfo(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`, meta.lang).monday); setCopyPreview(null); };
  const runCopyPreview = async () => {
    if (!copyTarget || copyTarget === meta.week_start) return toast.error(t("planning.copyChooseTarget"));
    setCopyBusy(true);
    try {
      const { data } = await api.post("/planning/entries/copy-week", { source_week: meta.week_start, target_week: copyTarget, scope: copyScope, mode: "preview", plan: isReferentDementia ? copyPlan : null });
      setCopyPreview(data);
      if (!data.source_count) toast.info(t("planning.copyNothing"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("planning.copyFailed")); }
    setCopyBusy(false);
  };
  const runCopy = async (mode) => {
    setCopyBusy(true);
    try {
      const { data } = await api.post("/planning/entries/copy-week", { source_week: meta.week_start, target_week: copyTarget, scope: copyScope, mode, plan: isReferentDementia ? copyPlan : null });
      toast.success(t("planning.copyDone", { count: data.copied, target: copyTarget }) + (data.skipped ? " · " + t("planning.copyDoneSkipped", { count: data.copied, skipped: data.skipped }) : ""));
      setCopyOpen(false); setCopyPreview(null);
      const w = weekInfo(copyTarget, meta.lang);
      setMeta((m) => ({ ...m, week_start: w.monday, period: w.period }));
    } catch (e) { toast.error(e?.response?.data?.detail || t("planning.copyFailed")); }
    setCopyBusy(false);
  };

  // Auto-remplissage : nom du groupe/résidence + animateur/fonction depuis l'utilisateur connecté
  useEffect(() => {
    if (!user) return;
    setMeta((m) => ({
      ...m,
      group: m.group === "Novadia" && user.group_name ? user.group_name : m.group,
      residence: m.residence || user.maison || "",
    }));
    if (isPro) {
      setForm((f) => ({ ...f, animators: f.animators || user.name || "", function: f.function || functionToFr(user.function || "") }));
    }
  }, [user, isPro]);

  const fetchLib = useCallback(async (name, autoDefault = true) => {
    const n = (name || "").trim();
    if (n.length < 2) { setLib([]); return; }
    setLibLoading(true);
    try {
      const res = await api.get("/planning/images", { params: { activity: n } });
      const imgs = res.data.images || [];
      setLib(imgs);
      if (autoDefault) {
        const def = imgs.find((i) => i.is_default);
        if (def) {
          setForm((f) => (f.image_id ? f : { ...f, image_id: def.id, image_url: def.url }));
          setDefaultPanel((p) => p || { fromCatalog: false });
        }
      }
    } catch { /* silencieux */ }
    setLibLoading(false);
  }, []);

  const onName = (name) => {
    const match = catalog.find((c) => normKey(c.name_fr) === normKey(name) || (c.name_nl && normKey(c.name_nl) === normKey(name)) || (c.name_en && normKey(c.name_en) === normKey(name)));
    setForm((f) => ({
      ...f, name: match ? match.name_fr : name,
      name_nl: match?.name_nl || "",
      name_en: match?.name_en || "",
      catalog_id: match?.id || "",
      objectives: match && (match.objectives?.length) && !(f.objectives?.length) ? [...match.objectives] : f.objectives,
      ...(match?.default_image_url ? { image_id: match.default_image_id, image_url: match.default_image_url } : {}),
    }));
    setDefaultPanel(match?.default_image_url ? { fromCatalog: true, catalogId: match.id } : null);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchLib(name), 400);
  };

  const generateAI = async () => {
    if (!form.name.trim()) return toast.error(t("planning.toastNameRequired"));
    setAiBusy(true);
    try {
      const { data } = await api.post("/planning/images/generate", { activity_name: form.name.trim(), style: photoStyle });
      setForm((f) => ({ ...f, image_id: data.id, image_url: data.url }));
      await fetchLib(form.name, false);
      toast.success(t("planning.aiImageDone"));
    } catch { toast.error(t("planning.aiImageFailed")); }
    setAiBusy(false);
  };



  const setSelectedAsDefault = async () => {
    if (!form.image_id) return;
    try { await api.post(`/planning/images/${form.image_id}/default`); toast.success(t("planning.imgSetDefault")); setDefaultPanel({ fromCatalog: false }); await fetchLib(form.name, false); }
    catch { toast.error(t("planning.errGeneric")); }
  };

  const clearDefaultAction = async () => {
    try {
      const libDef = lib.find((i) => i.is_default);
      if (libDef) await api.delete(`/planning/images/${libDef.id}/default`);
      if (defaultPanel?.fromCatalog && isAdmin && defaultPanel.catalogId) {
        const c = catalog.find((x) => x.id === defaultPanel.catalogId);
        if (c) await api.put(`/planning/catalog/activities/${defaultPanel.catalogId}`, { name_fr: c.name_fr, name_nl: c.name_nl, category_id: c.category_id, default_image_id: "" });
        setCatalog((prev) => prev.map((x) => (x.id === defaultPanel.catalogId ? { ...x, default_image_id: "", default_image_url: "" } : x)));
      } else if (defaultPanel?.fromCatalog && !isAdmin) {
        toast.info(t("planning.catalogDefaultInfo"));
      }
      setForm((f) => ({ ...f, image_id: "", image_url: "" }));
      setDefaultPanel(null);
      await fetchLib(form.name, false);
      toast.success(t("planning.imgDefaultRemoved"));
    } catch { toast.error(t("planning.errGeneric")); }
  };

  const googleImg = () => window.open("https://www.google.com/search?tbm=isch&q=" + encodeURIComponent(form.name || "animation seniors"), "_blank");

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    if (!form.name.trim()) return toast.error(t("planning.enterNameFirst"));
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const askDefault = window.confirm(`Enregistrer cette image comme image par défaut pour « ${form.name.trim()} » ?`);
      const res = await api.post("/planning/images", { activity_name: form.name.trim(), data_url: dataUrl, source: "upload", set_default: askDefault });
      const img = res.data;
      setForm((f) => ({ ...f, image_id: img.id, image_url: img.url }));
      await fetchLib(form.name, false);
      toast.success(askDefault ? "Image enregistrée comme défaut" : "Image ajoutée à la bibliothèque");
    } catch { toast.error(t("planning.imgImportFail")); }
    setUploading(false);
  };

  const selectImg = (img) => setForm((f) => ({ ...f, image_id: img.id, image_url: img.url }));
  const clearForis = () => setForm((f) => ({ ...f, image_id: "", image_url: "" }));

  const setAsDefault = async (img) => {
    try { await api.post(`/planning/images/${img.id}/default`); toast.success(t("planning.imgDefaultSet")); await fetchLib(form.name, false); }
    catch { toast.error(t("planning.errGeneric")); }
  };
  const removeDefault = async (img) => {
    try { await api.delete(`/planning/images/${img.id}/default`); toast.success(t("planning.defaultRemoved")); await fetchLib(form.name, false); }
    catch { toast.error(t("planning.errGeneric")); }
  };
  const removeImg = async (img) => {
    if (!window.confirm("Supprimer cette image de la bibliothèque ?")) return;
    try {
      await api.delete(`/planning/images/${img.id}`);
      if (form.image_id === img.id) clearForis();
      await fetchLib(form.name, false);
      toast.success(t("planning.imgDeleted"));
    } catch { toast.error(t("planning.errGeneric")); }
  };

  const resetForm = () => { setForm({ ...empty, day: form.day, moment: form.moment, animators: isPro ? (user?.name || "") : "", function: isPro ? functionToFr(user?.function || "") : "" }); setEditingId(null); setLib([]); localStorage.removeItem("cd_planning_draft"); };

  // Protection des données non enregistrées : brouillon automatique + avertissement navigateur
  useEffect(() => {
    const raw = localStorage.getItem("cd_planning_draft");
    if (raw) { try { const p = JSON.parse(raw); if (p && p.name && p.name.trim()) { setForm((f) => ({ ...f, ...p })); toast.info(t("planning.draftRestored")); } } catch {} }
  }, []);
  useEffect(() => {
    if (form.name && form.name.trim()) localStorage.setItem("cd_planning_draft", JSON.stringify(form));
  }, [form]);
  useEffect(() => {
    const h = (e) => { if (form.name && form.name.trim()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [form.name]);

  const add = async () => {
    if (!form.name.trim()) return toast.error(t("planning.toastNameRequired"));
    if (!meta.week_start) return toast.error(t("planning.toastChooseWeek"));
    const payload = {
      week_start: meta.week_start, day: form.day, moment: form.moment,
      name: form.name.trim(), name_nl: form.name_nl || "", name_en: form.name_en || "", catalog_id: form.catalog_id || "",
      theme: meta.theme, start: form.start || "", end: form.end || "", place: form.place || "",
      animators: form.animators || "", function: form.function || "",
      image_id: form.image_id || "", image_url: form.image_url || "",
      objectives: (form.objectives || []).map((s) => s.trim()).filter(Boolean),
      dedicated: !!form.dedicated,
    };
    try {
      if (editingId) {
        await api.put(`/planning/entries/${editingId}`, payload);
        toast.success(t("planning.toastActUpdated"));
      } else {
        await api.post("/planning/entries", payload);
        toast.success(t("planning.toastActSaved"));
      }
      resetForm();
      await loadEntries(meta.week_start);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("planning.toastSaveFailed"));
    }
  };

  const editEntry = (a) => {
    if (!a.can_edit) return;
    setEditingId(a.id);
    setForm({ name: a.name || "", name_nl: a.name_nl || "", name_en: a.name_en || "", catalog_id: a.catalog_id || "", start: a.start || "", end: a.end || "", place: a.place || "", animators: a.animators || "", function: functionToFr(a.function || ""), day: a.day || 0, moment: a.moment || "morning", image_id: a.image_id || "", image_url: a.image_url || "", objectives: a.objectives || [], dedicated: !!a.dedicated });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (a) => {
    if (!a.can_edit) return;
    if (!window.confirm(t("planning.toastConfirmDelete", { name: a.name }))) return;
    try { await api.delete(`/planning/entries/${a.id}`); toast.success(t("planning.toastActDeleted")); if (editingId === a.id) resetForm(); await loadEntries(meta.week_start); }
    catch (e) { toast.error(e?.response?.data?.detail || t("planning.toastDeleteFailed")); }
  };

  const generate = async (fmt = "pptx", langs = null) => {
    const useLangs = (langs && langs.length) ? langs : [meta.lang];
    setLoading(true);
    try {
      const res = await fetch(`${API}/planning/${fmt}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...meta, page_format: pageFormat, langs: useLangs, activities: filteredActs.map((a) => ({ ...a, image: "" })) }),
      });
      if (!res.ok) throw new Error("Erreur " + res.status);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `planning_${meta.residence || "residence"}.${fmt}`.replace(/\s+/g, "_");
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(fmt === "pdf" ? t("planning.toastPdfDone") : t("planning.toastPptxDone"));
    } catch (e) { toast.error(t("planning.toastGenFailed")); }
    setLoading(false);
  };

  const L = meta.lang === "nl" ? { m: "Ochtend", a: "Namiddag" } : { m: "Matin", a: "Après-midi" };
  const activityOptions = Array.from(new Set(
    meta.lang === "fr"
      ? [...catalog.map((c) => c.name_fr).filter(Boolean), ...ACTIVITIES]
      : catalog.map((c) => meta.lang === "nl" ? (c.name_nl || c.name_fr) : (c.name_en || c.name_fr)).filter(Boolean)
  ));

  // Filtres (métier / professionnel / recherche activité-thème)
  const funcChips = Array.from(new Set(acts.map((a) => a.function || a.created_by_function).filter(Boolean)));
  const profChips = Array.from(new Set(acts.map((a) => a.created_by_name).filter(Boolean)));
  const filtersActive = filters.func || filters.prof || filters.q;
  const filteredActs = acts.filter((a) =>
    (planView === "referent" ? a.dedicated : !a.dedicated) &&
    (!filters.func || normKey(a.function) === normKey(filters.func) || normKey(a.created_by_function) === normKey(filters.func)) &&
    (!filters.prof || a.created_by_name === filters.prof) &&
    (!filters.q || normKey(a.name).includes(normKey(filters.q)) || normKey(a.theme).includes(normKey(filters.q)))
  );
  const resetFilters = () => setFilters({ func: "", prof: "", q: "" });
  const cellActs = (di, mo) => filteredActs.filter((a) => a.day === di && a.moment === mo);
  const dispName = (a) => meta.lang === "nl" ? (a.name_nl || a.name) : meta.lang === "en" ? (a.name_en || a.name) : a.name;
  // Participation (statut réalisée / annulée + nb participants)
  const [partOpen, setPartOpen] = useState(false);
  const [partEntry, setPartEntry] = useState(null);
  const [bilanEntry, setBilanEntry] = useState(null);
  const [ficheEntryId, setFicheEntryId] = useState(null);
  const dragRef = useRef(null);
  const [moveDlg, setMoveDlg] = useState(null); // { entry, day, moment, mode, start, end }

  const onDropCell = (di, mo) => {
    const a = dragRef.current;
    dragRef.current = null;
    if (!a || !a.can_edit) return;
    if (a.day === di && a.moment === mo) return;
    setMoveDlg({ entry: a, day: di, moment: mo, mode: "keep", start: a.start || "", end: a.end || "" });
  };

  const confirmMove = async () => {
    const d = moveDlg;
    if (!d) return;
    const a = d.entry;
    const start = d.mode === "modify" ? d.start : (a.start || "");
    const end = d.mode === "modify" ? d.end : (a.end || "");
    const payload = {
      week_start: meta.week_start, day: d.day, moment: d.moment,
      name: a.name || "", name_nl: a.name_nl || "", name_en: a.name_en || "", catalog_id: a.catalog_id || "",
      theme: meta.theme, start, end, place: a.place || "",
      animators: a.animators || "", function: a.function || "",
      image_id: a.image_id || "", image_url: a.image_url || "",
      objectives: a.objectives || [],
    };
    try {
      await api.put(`/planning/entries/${a.id}`, payload);
      toast.success(t("planning.moveDone"));
      setMoveDlg(null);
      await loadEntries(meta.week_start);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("planning.toastSaveFailed"));
    }
  };
  const openBilan = (a) => {
    const [y, mo, d] = meta.week_start.split("-").map(Number);
    const dt = new Date(y, mo - 1, d); dt.setDate(dt.getDate() + (a.day || 0));
    const p = (n) => String(n).padStart(2, "0");
    const iso = `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
    setBilanEntry({ catalog_id: a.catalog_id, name: a.name, date: iso });
  };
  const [partForm, setPartForm] = useState({ status: "done", participants: "", observation: "", cancel_reason: "" });
  const [partBusy, setPartBusy] = useState(false);
  // Vues Jour / Semaine / Mois
  const [view, setView] = useState("week");
  const [dayIdx, setDayIdx] = useState(() => { const wd = new Date().getDay(); return wd === 0 ? 6 : wd - 1; });
  const [monthEntries, setMonthEntries] = useState([]);
  const monthAnchor = meta.week_start;
  useEffect(() => {
    if (view !== "month") return;
    const [y, m] = monthAnchor.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    api.get("/planning/entries/range", { params: { frm: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(last)}` } })
      .then(({ data }) => setMonthEntries(data.entries || [])).catch(() => setMonthEntries([]));
  }, [view, monthAnchor, partOpen]);
  const monthCells = () => {
    const [y, m] = monthAnchor.split("-").map(Number);
    const firstDow = (new Date(y, m - 1, 1).getDay() + 6) % 7; // lundi=0
    const days = new Date(y, m, 0).getDate();
    const byDate = {};
    monthEntries.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= days; d++) { const iso = `${y}-${pad(m)}-${pad(d)}`; cells.push({ d, iso, items: byDate[iso] || [] }); }
    return cells;
  };
  const openDayFromDate = (iso) => { const w = weekInfo(iso, meta.lang); setMeta((mm) => ({ ...mm, week_start: w.monday, period: w.period })); const [yy, mm2, dd] = iso.split("-").map(Number); const wd = new Date(yy, mm2 - 1, dd).getDay(); setDayIdx(wd === 0 ? 6 : wd - 1); setView("day"); };
  const openParticipation = (a) => {
    setPartEntry(a);
    setPartForm({ status: a.status === "cancelled" ? "cancelled" : "done", participants: a.participants ?? "", observation: a.observation || "", cancel_reason: a.cancel_reason || "" });
    setPartOpen(true);
  };
  const submitParticipation = async () => {
    if (partForm.status === "done" && (partForm.participants === "" || Number(partForm.participants) < 0)) return toast.error(t("planning.enterParticipants"));
    setPartBusy(true);
    try {
      await api.post(`/planning/entries/${partEntry.id}/participation`, {
        status: partForm.status,
        participants: partForm.status === "done" ? Number(partForm.participants) : null,
        observation: partForm.observation,
        cancel_reason: partForm.cancel_reason,
      });
      toast.success(t("planning.participationSaved"));
      setPartOpen(false);
      await loadEntries(meta.week_start);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'enregistrement"); }
    setPartBusy(false);
  };
  const fnOptions = functionOptions(meta.lang);

  return (
    <div className="space-y-6" data-testid="planning-page">
      <div className="dash-card p-6" style={{ background: `linear-gradient(120deg, ${THEME_HEX[meta.theme]}22, transparent)` }}>
        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ color: THEME_HEX[meta.theme], background: `${THEME_HEX[meta.theme]}22` }}>
          <CalendarDays className="h-3.5 w-3.5" /> {t("planning.badge")}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">{t("planning.title")}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{t("planning.subtitle")}</p>
      </div>

      <Card className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div><Label>{t("planning.group")}</Label><Input data-testid="pl-group" value={meta.group} onChange={e => setMeta({ ...meta, group: e.target.value })} /></div>
        <div><Label>{t("planning.residence")}</Label><Input data-testid="pl-residence" value={meta.residence} onChange={e => setMeta({ ...meta, residence: e.target.value })} placeholder="Résidence Senior's Serenity" /></div>
        <div className="md:col-span-1"><Label>{t("planning.weekStart")}</Label>
          <div className="flex items-center gap-1">
            <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid="pl-week-prev" onClick={() => shiftWeek(-7)} title={t("planning.weekPrev")}><ChevronLeft className="h-4 w-4" /></Button>
            <Input data-testid="pl-weekstart" type="date" className="min-w-0" value={meta.week_start || ""} onChange={e => { const w = weekInfo(e.target.value, meta.lang); setMeta({ ...meta, week_start: w.monday, period: w.period }); }} />
            <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid="pl-week-next" onClick={() => shiftWeek(7)} title={t("planning.weekNext")}><ChevronRight className="h-4 w-4" /></Button>
            <Popover>
              <PopoverTrigger asChild><Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid="pl-week-calendar" title={t("planning.calendar")}><CalendarDays className="h-4 w-4" /></Button></PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" weekStartsOn={1} selected={meta.week_start ? new Date(meta.week_start + "T00:00:00") : undefined}
                  onSelect={(d) => { if (!d) return; const p = (n) => String(n).padStart(2, "0"); const w = weekInfo(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`, meta.lang); setMeta((m) => ({ ...m, week_start: w.monday, period: w.period })); }} />
              </PopoverContent>
            </Popover>
            <Button type="button" variant="ghost" size="sm" className="shrink-0" data-testid="pl-week-today" onClick={() => { const w = weekInfo(todayISO(), meta.lang); setMeta((m) => ({ ...m, week_start: w.monday, period: w.period })); }}>{t("planning.today")}</Button>
          </div>
        </div>
        <div><Label>{t("planning.period")}</Label><Input data-testid="pl-period" value={meta.period} onChange={e => setMeta({ ...meta, period: e.target.value })} placeholder="Du lundi 03 au dimanche 09 août 2026" /></div>
        <div><Label>{t("planning.footerMsg")}</Label><Input data-testid="pl-footer" value={meta.footer} onChange={e => setMeta({ ...meta, footer: e.target.value })} /></div>
        <div><Label>{t("planning.theme")}</Label>
          <div className="flex items-center gap-2">
            <select data-testid="pl-theme" className="w-full h-10 rounded-md border px-3 bg-background" value={meta.theme} onChange={e => setMeta({ ...meta, theme: e.target.value })}>
              {THEMES.map(th => <option key={th} value={th}>{themeLabel(th, meta.lang)}</option>)}
            </select>
            <span className="h-8 w-8 rounded-md border shrink-0" style={{ background: THEME_HEX[meta.theme] }} />
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">{t("planning.addActivity")}</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2"><Label>{t("planning.actName")}</Label><Input list="act-list" data-testid="pl-name" value={form.name} onChange={e => onName(e.target.value)} placeholder="Gym douce" />
            <datalist id="act-list">{activityOptions.map(a => <option key={a} value={a} />)}</datalist>
          </div>
          <div><Label>{t("planning.start")}</Label><Input data-testid="pl-start" value={form.start} onChange={e => setForm({ ...form, start: e.target.value })} placeholder="10h30" /></div>
          <div><Label>{t("planning.end")}</Label><Input data-testid="pl-end" value={form.end} onChange={e => setForm({ ...form, end: e.target.value })} placeholder="11h30" /></div>
          <div><Label>{t("planning.place")}</Label><Input data-testid="pl-place" value={form.place} onChange={e => setForm({ ...form, place: e.target.value })} placeholder="Salon du 3e" /></div>
          <div><Label>{t("planning.animators")}</Label><Input data-testid="pl-anim" value={form.animators} onChange={e => setForm({ ...form, animators: e.target.value })} placeholder="Emmanuel Aitopou" /></div>
          <div><Label>{t("planning.function")}</Label>
            <select data-testid="pl-func" className="w-full h-10 rounded-md border px-3 bg-background" value={fnOptions.some((o) => o.value === form.function) ? form.function : (form.function || "")} onChange={e => setForm({ ...form, function: e.target.value })}>
              <option value="">—</option>
              {!fnOptions.some((o) => o.value === form.function) && form.function ? <option value={form.function}>{form.function}</option> : null}
              {fnOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {((isReferentDementia && normKey(form.function).includes("referent") && normKey(form.function).includes("demence")) || (!isReferentDementia && canDedicated)) && (
            <div className="sm:col-span-2 rounded-lg border p-3 bg-violet-500/[0.05]" data-testid="pl-dedicated-toggle">
              <Label className="flex items-center gap-2 text-violet-700">{t("planning.dedicatedTitle")}</Label>
              <div className="flex gap-2 mt-2">
                <button type="button" data-testid="pl-dedicated-common" onClick={() => setForm({ ...form, dedicated: false })}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold border transition ${!form.dedicated ? "bg-teal-500 text-white border-teal-500" : "bg-background hover:bg-muted"}`}>{t("planning.dedicatedCommon")}</button>
                <button type="button" data-testid="pl-dedicated-own" onClick={() => setForm({ ...form, dedicated: true })}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold border transition ${form.dedicated ? "bg-violet-600 text-white border-violet-600" : "bg-background hover:bg-muted"}`}>{isReferentDementia ? t("planning.dedicatedOwn") : t("planning.dedicatedDelegate")}</button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">{form.dedicated ? (isReferentDementia ? t("planning.dedicatedOwnHint") : t("planning.dedicatedDelegateHint")) : t("planning.dedicatedCommonHint")}</p>
            </div>
          )}
          <div><Label>{t("planning.day")}</Label>
            <select data-testid="pl-day" className="w-full h-10 rounded-md border px-3 bg-background" value={form.day} onChange={e => setForm({ ...form, day: Number(e.target.value) })}>
              {DAYS.map((d, i) => <option key={i} value={i}>{dayName(i, meta.lang)}</option>)}
            </select>
          </div>
          <div><Label>{t("planning.moment")}</Label>
            <select data-testid="pl-moment" className="w-full h-10 rounded-md border px-3 bg-background" value={form.moment} onChange={e => setForm({ ...form, moment: e.target.value })}>
              <option value="morning">{L.m}</option><option value="afternoon">{L.a}</option>
            </select>
          </div>
        </div>

        <PlanningRecoPanel catalogId={form.catalog_id} objectives={form.objectives || []} activityName={form.name || ""} />

        <ObjectivesEditor
          objectives={form.objectives || []}
          onChange={(objs) => setForm((f) => ({ ...f, objectives: objs }))}
          otypes={otypes}
          t={t}
        />

        {/* Bibliothèque & sources d'images */}
        <div className="mt-4 rounded-lg border p-3 bg-muted/30" data-testid="pl-image-library">
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="mr-1 flex items-center gap-1"><ImageIcon className="h-4 w-4" /> {t("planning.image")}</Label>
            <Button type="button" variant="outline" size="sm" data-testid="pl-ai-generate" onClick={generateAI} disabled={aiBusy}>
              {aiBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} {t("planning.generateAI")}
            </Button>
            <div className="inline-flex items-center rounded-full border overflow-hidden text-xs" data-testid="pl-ai-style">
              <button type="button" data-testid="pl-ai-style-photo" onClick={() => setPhotoStyle("photo")} className={`px-3 py-1.5 transition-colors ${photoStyle === "photo" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{t("planning.stylePhoto")}</button>
              <button type="button" data-testid="pl-ai-style-illustration" onClick={() => setPhotoStyle("illustration")} className={`px-3 py-1.5 transition-colors ${photoStyle === "illustration" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{t("planning.styleIllustration")}</button>
            </div>
            <Button type="button" variant="outline" size="sm" data-testid="pl-google" onClick={googleImg}>{t("planning.google")}</Button>
            <label className="inline-flex items-center gap-1 text-sm border rounded-md px-3 py-1.5 cursor-pointer hover:bg-accent" data-testid="pl-upload-label">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("planning.import")}
              <input type="file" accept="image/*" className="hidden" data-testid="pl-upload" onChange={onUpload} disabled={uploading} />
            </label>
            {form.image_url && <img src={imgSrc(form.image_url)} alt="" className="h-12 w-12 rounded object-cover border" data-testid="pl-img-preview" />}
            {form.image_id && <Button type="button" variant="ghost" size="sm" onClick={setSelectedAsDefault} data-testid="pl-set-default"><Star className="h-3.5 w-3.5 mr-1" /> {t("planning.setDefault")}</Button>}
            {form.image_id && <button type="button" className="text-xs text-red-500" onClick={clearForis} data-testid="pl-img-clear">{t("planning.remove")}</button>}
          </div>

          {defaultPanel && form.image_url && (
            <div className="mt-3 rounded-md border border-amber-400/40 bg-amber-500/10 p-2.5 flex items-center gap-2 flex-wrap" data-testid="pl-default-panel">
              <span className="text-xs font-medium flex items-center gap-1"><Star className="h-3.5 w-3.5 text-amber-500 fill-current" /> {t("planning.defaultApplied", { name: form.name.trim() })}</span>
              <Button type="button" size="sm" variant="secondary" onClick={() => setDefaultPanel(null)} data-testid="pl-def-keep">{t("planning.keep")}</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => { setDefaultPanel(null); toast.info(t("planning.chooseOtherImg")); }} data-testid="pl-def-change-once">{t("planning.changeThisTime")}</Button>
              <Button type="button" size="sm" variant="outline" onClick={setSelectedAsDefault} data-testid="pl-def-set-new">{t("planning.setAsNewDefault")}</Button>
              <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={clearDefaultAction} data-testid="pl-def-remove">{t("planning.deleteDefaultImg")}</Button>
            </div>
          )}

          {aiBusy && <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> {t("planning.aiGenerating")}</div>}

          {libLoading && <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> {t("planning.libLoading")}</div>}
          {!libLoading && form.name.trim().length >= 2 && lib.length === 0 && (
            <div className="mt-3 text-xs text-muted-foreground" data-testid="pl-lib-empty">{t("planning.noImgFor", { name: form.name.trim() })}</div>
          )}
          {lib.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium mb-2 text-muted-foreground">{t("planning.libFor", { name: form.name.trim() })}</div>
              <div className="flex flex-wrap gap-2" data-testid="pl-lib-grid">
                {lib.map((img) => (
                  <div key={img.id} className={`relative group rounded-md border-2 overflow-hidden ${form.image_id === img.id ? "border-primary" : "border-transparent"}`} data-testid={`pl-lib-img-${img.id}`}>
                    <img src={imgSrc(img.url)} alt="" className="h-16 w-16 object-cover cursor-pointer" onClick={() => selectImg(img)} />
                    {img.is_default && <span className="absolute top-0 left-0 bg-yellow-400 text-[9px] px-1 rounded-br font-bold flex items-center gap-0.5"><Star className="h-2.5 w-2.5 fill-current" />{t("planning.defaultBadge")}</span>}
                    <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity py-0.5">
                      {img.is_default
                        ? <button title={t("planning.removeDefault")} className="text-white" onClick={() => removeDefault(img)} data-testid={`pl-undefault-${img.id}`}><Star className="h-3.5 w-3.5 fill-current text-yellow-400" /></button>
                        : <button title={t("planning.setDefault")} className="text-white" onClick={() => setAsDefault(img)} data-testid={`pl-default-${img.id}`}><Star className="h-3.5 w-3.5" /></button>}
                      <button title={t("planning.deleteImg")} className="text-white" onClick={() => removeImg(img)} data-testid={`pl-remove-${img.id}`}><X className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button data-testid="pl-add" onClick={add}>
            {editingId ? <><Pencil className="h-4 w-4 mr-1" /> {t("planning.editSave")}</> : <><Plus className="h-4 w-4 mr-1" /> {t("planning.add")}</>}
          </Button>
          {editingId && <Button variant="ghost" data-testid="pl-cancel-edit" onClick={resetForm}>{t("planning.cancel")}</Button>}
          {!editingId && <span className="text-xs text-muted-foreground">{t("planning.addHint")}</span>}
        </div>
      </Card>

      <Card className="p-5" data-testid="pl-week-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-semibold">{t("planning.weekPreview")} — {meta.residence || t("planning.residence")} <span className="text-muted-foreground font-normal">({filteredActs.length}{filtersActive ? ` / ${acts.length}` : ""})</span></h3>
            <p className="text-xs text-muted-foreground">{meta.period}{loadingEntries ? ` · ${t("planning.loading")}` : ""}</p>
            <div className="inline-flex mt-2 rounded-lg bg-muted p-0.5" data-testid="pl-planview-switch">
              <button data-testid="pl-planview-commun" onClick={() => setPlanView("commun")}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition ${planView === "commun" ? "bg-white shadow-sm text-teal-700" : "text-muted-foreground hover:text-foreground"}`}>{t("planning.viewCommon")}</button>
              <button data-testid="pl-planview-referent" onClick={() => setPlanView("referent")}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition ${planView === "referent" ? "bg-white shadow-sm text-violet-700" : "text-muted-foreground hover:text-foreground"}`}>{t("planning.viewReferent")}</button>
            </div>
            {isReferentDementia && (
              <Button size="sm" variant="outline" className="ml-2 mt-2 border-violet-300 text-violet-700 hover:bg-violet-50 hover:text-violet-800" data-testid="pl-access-btn" onClick={openAccess}>
                <Users className="h-4 w-4 mr-1.5" /> {t("planning.accessBtn")}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border p-0.5 bg-muted/40" data-testid="pl-view-switch">
              {[["day", t("planning.viewDay")], ["week", t("planning.viewWeek")], ["month", t("planning.viewMonth")]].map(([v, lbl]) => (
                <button key={v} data-testid={`pl-view-${v}`} onClick={() => setView(v)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${view === v ? "bg-white shadow-sm text-teal-700" : "text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
              ))}
            </div>
            <Button data-testid="pl-copy-week" variant="outline" onClick={openCopy}>
              <Copy className="h-4 w-4 mr-1" /> {t("planning.copyWeek")}
            </Button>
            <Button data-testid="pl-preview" variant="outline" onClick={() => { setPreviewLangs([meta.lang]); setShowPreview(true); }} disabled={!filteredActs.length}>
              <Eye className="h-4 w-4 mr-1" /> {t("planning.preview")}
            </Button>
            <div className="inline-flex rounded-lg border overflow-hidden" data-testid="pl-format">
              {["A4", "A3"].map((f) => (
                <button key={f} data-testid={`pl-format-${f}`} onClick={() => setPageFormat(f)}
                  className={`px-3 py-1.5 text-sm font-semibold transition ${pageFormat === f ? "bg-sky-500 text-white" : "bg-transparent text-slate-600 hover:bg-slate-100"}`}>
                  {f}
                </button>
              ))}
            </div>
            <Button data-testid="pl-pdf" variant="outline" onClick={() => generate("pdf")} disabled={loading || !filteredActs.length}>
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} {t("planning.pdf")}
            </Button>
          </div>
        </div>

        {/* Navigation de semaine directement dans l'aperçu (comme le sélecteur du haut) */}
        <div className="mb-4 flex items-center justify-center gap-1.5 flex-wrap" data-testid="pl-week-nav-preview">
          <Button type="button" variant="outline" size="icon" className="h-9 w-9" data-testid="pl-nav-prev" onClick={() => shiftWeek(-7)} title={t("planning.weekPrev")}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-semibold min-w-[150px] text-center text-teal-700">{meta.week_start ? new Date(meta.week_start + "T00:00:00").toLocaleDateString("fr-FR") : "—"}</span>
          <Button type="button" variant="outline" size="icon" className="h-9 w-9" data-testid="pl-nav-next" onClick={() => shiftWeek(7)} title={t("planning.weekNext")}><ChevronRight className="h-4 w-4" /></Button>
          <Popover>
            <PopoverTrigger asChild><Button type="button" variant="outline" size="icon" className="h-9 w-9" data-testid="pl-nav-calendar" title={t("planning.calendar")}><CalendarDays className="h-4 w-4" /></Button></PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center">
              <Calendar mode="single" weekStartsOn={1} selected={meta.week_start ? new Date(meta.week_start + "T00:00:00") : undefined}
                onSelect={(d) => { if (!d) return; const p = (n) => String(n).padStart(2, "0"); const w = weekInfo(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`, meta.lang); setMeta((m) => ({ ...m, week_start: w.monday, period: w.period })); }} />
            </PopoverContent>
          </Popover>
          <Button type="button" variant="ghost" size="sm" data-testid="pl-nav-today" onClick={() => { const w = weekInfo(todayISO(), meta.lang); setMeta((m) => ({ ...m, week_start: w.monday, period: w.period })); }}>{t("planning.today")}</Button>
        </div>

        {/* Filtres */}
        <div className="mb-4 rounded-lg border p-3 bg-muted/30" data-testid="pl-filters">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold flex items-center gap-1 text-muted-foreground"><Filter className="h-3.5 w-3.5" /> {t("planning.filters")}</span>
            <button data-testid="pl-filter-all" onClick={() => setFilters((f) => ({ ...f, func: "" }))} className={`text-xs rounded-full px-3 py-1 border transition-colors ${!filters.func ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"}`}>{t("planning.all")}</button>
            {funcChips.map((fn) => (
              <button key={fn} data-testid={`pl-filter-func-${normKey(fn)}`} onClick={() => setFilters((f) => ({ ...f, func: f.func === fn ? "" : fn }))} className={`text-xs rounded-full px-3 py-1 border transition-colors ${filters.func === fn ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"}`}>{functionLabel(fn, meta.lang)}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <select data-testid="pl-filter-prof" className="h-9 rounded-md border px-2 text-sm bg-background" value={filters.prof} onChange={(e) => setFilters((f) => ({ ...f, prof: e.target.value }))}>
              <option value="">{t("planning.allPros")}</option>
              {profChips.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="pl-filter-q" className="h-9 pl-7 w-56" placeholder={t("planning.searchActTheme")} value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
            </div>
            {filtersActive && <Button variant="ghost" size="sm" data-testid="pl-filter-reset" onClick={resetFilters}><RotateCcw className="h-3.5 w-3.5 mr-1" /> {t("planning.resetFilters")}</Button>}
          </div>
        </div>

        {view === "day" && (
          <div className="mb-3 flex items-center justify-center gap-3" data-testid="pl-day-nav">
            <Button variant="outline" size="icon" onClick={() => setDayIdx((i) => (i + 6) % 7)}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="font-semibold text-teal-700 min-w-[140px] text-center">{dayName(dayIdx, meta.lang)}</span>
            <Button variant="outline" size="icon" onClick={() => setDayIdx((i) => (i + 1) % 7)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}

        {view === "month" ? (
          <div data-testid="pl-month-view">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => <div key={d} className="text-center text-xs font-bold text-muted-foreground py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {monthCells().map((c, i) => c === null ? <div key={i} /> : (
                <button key={i} onClick={() => openDayFromDate(c.iso)} data-testid={`pl-month-day-${c.d}`}
                  className="text-left border rounded-lg p-1.5 min-h-[86px] hover:border-teal-400 hover:bg-teal-50/40 transition">
                  <div className="text-xs font-bold text-slate-500">{c.d}</div>
                  {c.items.slice(0, 3).map((a) => (
                    <div key={a.id} className="mt-0.5 truncate text-[10px] rounded px-1 py-0.5" style={{ background: `${THEME_HEX[meta.theme]}22`, color: THEME_HEX[meta.theme] }}>{dispName(a)}</div>
                  ))}
                  {c.items.length > 3 && <div className="text-[9px] text-muted-foreground mt-0.5">+{c.items.length - 3}</div>}
                </button>
              ))}
            </div>
          </div>
        ) : (
        <div className={`grid grid-cols-1 gap-2 ${view === "week" ? "md:grid-cols-7" : ""}`}>
          {(view === "day" ? [dayIdx] : [0, 1, 2, 3, 4, 5, 6]).map((di) => (
            <div key={di} className="border rounded-lg overflow-hidden">
              <div className="text-white text-center text-sm font-semibold py-1.5" style={{ background: THEME_HEX[meta.theme] }}>{dayName(di, meta.lang)}</div>
              {["morning", "afternoon"].map(mo => (
                <div key={mo} className="p-1.5 border-t min-h-[70px]" style={{ background: `${THEME_HEX[meta.theme]}11` }}
                  onDragOver={(e) => { if (dragRef.current) e.preventDefault(); }}
                  onDrop={() => onDropCell(di, mo)} data-testid={`pl-cell-${di}-${mo}`}>
                  <div className="text-[10px] uppercase font-bold opacity-60">{mo === "morning" ? L.m : L.a}</div>
                  {cellActs(di, mo).map((a) => (
                    <div key={a.id} draggable={!!a.can_edit}
                      onDragStart={(e) => { if (a.can_edit) { dragRef.current = a; try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", a.id); } catch (err) {} } else { e.preventDefault(); } }}
                      onDragEnd={() => { dragRef.current = null; }}
                      className={`mt-1 text-xs rounded p-1 flex justify-between gap-1 group text-slate-800 ${a.can_edit ? "bg-white/90 cursor-grab active:cursor-grabbing" : "bg-white/60"}`} data-testid={`pl-entry-${a.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold leading-tight flex items-center gap-1">{!a.can_edit && <Lock className="h-2.5 w-2.5 opacity-50 shrink-0" />}<span className="truncate">{dispName(a)}</span></div>
                        <div className="opacity-60">{[a.start && a.end ? `${a.start}-${a.end}` : "", a.place].filter(Boolean).join(" · ")}</div>
                        {a.animators && <div className="opacity-70">{t("planning.withWord")} {a.animators}</div>}
                        {a.created_by_name && <div className="mt-0.5 inline-flex items-center gap-1 rounded px-1 py-0.5 text-[9px] font-medium" style={{ background: `${THEME_HEX[meta.theme]}22`, color: THEME_HEX[meta.theme] }} title={`Créé par ${a.created_by_name}${a.last_modified_by_name && a.last_modified_by_name !== a.created_by_name ? ` · modifié par ${a.last_modified_by_name}` : ""}`}>{functionLabel(a.created_by_function, meta.lang) || "—"} · {a.created_by_name}</div>}
                        {a.image_url && <img src={imgSrc(a.image_url)} alt="" draggable={false} className="mt-1 w-full h-10 object-cover rounded pointer-events-none" />}
                        {(() => {
                          const past = a.date && a.date < todayISO();
                          if (a.status === "done") return <div className="mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-700" data-testid={`pl-status-${a.id}`}><CheckCircle2 className="h-2.5 w-2.5" />{t("planning.doneWithCount", { count: a.participants })}</div>;
                          if (a.status === "cancelled") return <div className="mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold bg-red-100 text-red-600" data-testid={`pl-status-${a.id}`}><Ban className="h-2.5 w-2.5" />Annulée</div>;
                          if (past) return <div className="mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-700" data-testid={`pl-status-${a.id}`}><AlertTriangle className="h-2.5 w-2.5" />{t("planning.toEncode")}</div>;
                          return null;
                        })()}
                      </div>
                      {a.can_edit ? (
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 shrink-0">
                          <button data-testid={`pl-part-${a.id}`} onClick={() => openParticipation(a)} className="text-emerald-600" title={t("planning.encodeParticipation")}><CheckCircle2 className="h-3.5 w-3.5" /></button>
                          {a.catalog_id ? <button data-testid={`pl-bilan-${a.id}`} onClick={() => openBilan(a)} className="text-teal-600" title={t("pp.bilan.title")}><ClipboardList className="h-3.5 w-3.5" /></button> : null}
                          <button data-testid={`pl-fiche-${a.id}`} onClick={() => setFicheEntryId(a.id)} className="text-indigo-500" title={t("pp.fiche.title")}><FileText className="h-3.5 w-3.5" /></button>
                          <button data-testid={`pl-edit-${a.id}`} onClick={() => editEntry(a)} className="text-blue-600" title={t("planning.editAction")}><Pencil className="h-3.5 w-3.5" /></button>
                          <button data-testid={`pl-del-${a.id}`} onClick={() => del(a)} className="text-red-500" title={t("planning.deleteAction")}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : (
                        <span className="text-[8px] text-muted-foreground shrink-0 self-start" title={t("planning.readOnly")}>{t("planning.readOnly")}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
        )}
      </Card>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="h-4 w-4" /> {t("planning.preview")}</DialogTitle>
            <DialogDescription>{t("planning.previewLangQuestion")}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 flex-wrap" data-testid="pl-preview-langs">
            {[["fr", "FR"], ["nl", "NL"], ["en", "EN"]].map(([lng, lbl]) => (
              <button key={lng} data-testid={`pl-preview-lang-${lng}`} onClick={() => togglePreviewLang(lng)}
                className={`text-sm rounded-full px-4 py-1.5 border transition-colors ${previewLangs.includes(lng) ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"}`}>{lbl}</button>
            ))}
          </div>
          <div className="space-y-6 mt-2">
            {previewLangs.map((lng) => (
              <div key={lng}>
                <div className="text-xs font-semibold text-muted-foreground mb-1">{lng.toUpperCase()}</div>
                <AffichePreview meta={metaForLang(lng)} acts={filteredActs} />
              </div>
            ))}
          </div>
          <div className="flex justify-end items-center gap-2 mt-2">
            <div className="inline-flex rounded-lg border overflow-hidden" data-testid="pl-preview-format">
              {["A4", "A3"].map((f) => (
                <button key={f} data-testid={`pl-preview-format-${f}`} onClick={() => setPageFormat(f)}
                  className={`px-3 py-1.5 text-sm font-semibold transition ${pageFormat === f ? "bg-sky-500 text-white" : "bg-transparent text-slate-600 hover:bg-slate-100"}`}>
                  {f}
                </button>
              ))}
            </div>
            <Button variant="outline" onClick={() => generate("pdf", previewLangs)} disabled={loading} data-testid="pl-preview-pdf"><Download className="h-4 w-4 mr-1" /> PDF{previewLangs.length > 1 ? ` (${previewLangs.length})` : ""}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={partOpen} onOpenChange={setPartOpen}>
        <DialogContent className="max-w-md" data-testid="pl-part-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Suivi de participation</DialogTitle>
            <DialogDescription>{partEntry ? dispName(partEntry) : ""}{partEntry?.start ? ` · ${partEntry.start}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="pl-part-done" onClick={() => setPartForm((f) => ({ ...f, status: "done" }))} className={`rounded-lg border p-3 text-sm font-semibold flex items-center justify-center gap-1 ${partForm.status === "done" ? "bg-emerald-600 text-white border-emerald-600" : "hover:bg-accent"}`}><CheckCircle2 className="h-4 w-4" /> {t("planning.doneShort")}</button>
              <button type="button" data-testid="pl-part-cancelled" onClick={() => setPartForm((f) => ({ ...f, status: "cancelled" }))} className={`rounded-lg border p-3 text-sm font-semibold flex items-center justify-center gap-1 ${partForm.status === "cancelled" ? "bg-red-600 text-white border-red-600" : "hover:bg-accent"}`}><Ban className="h-4 w-4" /> {t("planning.cancelledBadge")}</button>
            </div>
            {partForm.status === "done" ? (
              <div><Label>{t("planning.participantsCount")}</Label><Input type="number" min="0" data-testid="pl-part-count" value={partForm.participants} onChange={(e) => setPartForm((f) => ({ ...f, participants: e.target.value }))} placeholder={t("planning.egCount")} autoFocus /></div>
            ) : (
              <div><Label>{t("planning.cancelReason")}</Label><Input data-testid="pl-part-reason" value={partForm.cancel_reason} onChange={(e) => setPartForm((f) => ({ ...f, cancel_reason: e.target.value }))} placeholder={t("planning.egWeather")} /></div>
            )}
            <div><Label>{t("planning.observationOpt")}</Label><Input data-testid="pl-part-observation" value={partForm.observation} onChange={(e) => setPartForm((f) => ({ ...f, observation: e.target.value }))} placeholder={t("planning.shortNote")} /></div>
            {partEntry?.participation_by && <p className="text-[11px] text-muted-foreground">{t("planning.lastEncoding", { by: partEntry.participation_by })}{partEntry.participation_log?.length ? ` · ${partEntry.participation_log.length} modif.` : ""}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPartOpen(false)}>{t("planning.cancel")}</Button>
            <Button data-testid="pl-part-save" onClick={submitParticipation} disabled={partBusy}>{partBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BilanDialog open={!!bilanEntry} onClose={() => setBilanEntry(null)}
        catalogId={bilanEntry?.catalog_id} activityName={bilanEntry?.name} activityDate={bilanEntry?.date} />
      <ActivityFicheDialog open={!!ficheEntryId} onClose={() => setFicheEntryId(null)} entryId={ficheEntryId} />

      <Dialog open={!!moveDlg} onOpenChange={(o) => { if (!o) setMoveDlg(null); }}>
        <DialogContent className="max-w-md" data-testid="pl-move-dialog">
          <DialogHeader>
            <DialogTitle>{t("planning.moveTitle")}</DialogTitle>
            <DialogDescription>
              {moveDlg && t("planning.moveDesc", { name: dispName(moveDlg.entry), day: dayName(moveDlg.day, meta.lang), moment: moveDlg.moment === "morning" ? L.m : L.a })}
            </DialogDescription>
          </DialogHeader>
          {moveDlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="pl-move-keep" onClick={() => setMoveDlg((d) => ({ ...d, mode: "keep" }))}
                  className={`rounded-lg border p-3 text-sm text-left transition ${moveDlg.mode === "keep" ? "border-teal-500 bg-teal-50 text-teal-800" : "hover:bg-accent"}`}>
                  <div className="font-semibold">{t("planning.moveKeep")}</div>
                  <div className="text-xs text-muted-foreground">{moveDlg.entry.start || moveDlg.entry.end ? `${moveDlg.entry.start || "—"}${moveDlg.entry.end ? " – " + moveDlg.entry.end : ""}` : t("planning.moveNoTime")}</div>
                </button>
                <button type="button" data-testid="pl-move-modify" onClick={() => setMoveDlg((d) => ({ ...d, mode: "modify" }))}
                  className={`rounded-lg border p-3 text-sm text-left transition ${moveDlg.mode === "modify" ? "border-teal-500 bg-teal-50 text-teal-800" : "hover:bg-accent"}`}>
                  <div className="font-semibold">{t("planning.moveModify")}</div>
                  <div className="text-xs text-muted-foreground">{t("planning.moveModifyHint")}</div>
                </button>
              </div>
              {moveDlg.mode === "modify" && (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t("planning.start")}</Label><Input data-testid="pl-move-start" value={moveDlg.start} onChange={(e) => setMoveDlg((d) => ({ ...d, start: e.target.value }))} placeholder="10h30" /></div>
                  <div><Label>{t("planning.end")}</Label><Input data-testid="pl-move-end" value={moveDlg.end} onChange={(e) => setMoveDlg((d) => ({ ...d, end: e.target.value }))} placeholder="11h30" /></div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="pl-move-cancel" onClick={() => setMoveDlg(null)}>{t("planning.cancel")}</Button>
            <Button data-testid="pl-move-confirm" onClick={confirmMove} className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold">{t("planning.validate")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700"><Users className="h-5 w-5" /> {t("planning.accessTitle")}</DialogTitle>
            <DialogDescription>{t("planning.accessHint")}</DialogDescription>
          </DialogHeader>
          {delegates === null ? (
            <div className="py-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : delegates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t("planning.accessEmpty")}</p>
          ) : (
            <div className="rounded-xl border divide-y" data-testid="pl-access-list">
              {delegates.map((d) => (
                <div key={d.username} className="flex items-center gap-3 p-3" data-testid={`pl-access-row-${d.username}`}>
                  <div className="h-9 w-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold text-sm shrink-0">{(d.name || d.username).slice(0, 2).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{d.name || d.username}</div>
                    <div className="text-xs text-muted-foreground truncate">{functionLabel(d.function, meta.lang) || d.function}</div>
                  </div>
                  {d.granted && <Badge className="bg-violet-600 text-white text-[10px]">{t("planning.dedicatedTitle")}</Badge>}
                  <Switch data-testid={`pl-access-switch-${d.username}`} checked={d.granted} onCheckedChange={(v) => toggleDelegate(d.username, v)} />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="max-w-lg" data-testid="pl-copy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="h-4 w-4" /> {t("planning.copyWeekTitle")}</DialogTitle>
            <DialogDescription>{t("planning.copyWeekIntro")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("planning.copySource")}</Label>
              <Input value={weekInfo(meta.week_start, meta.lang).period || meta.week_start} disabled data-testid="pl-copy-source" />
            </div>
            <div>
              <Label>{t("planning.copyTarget")}</Label>
              <div className="flex items-center gap-1">
                <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid="pl-copy-prev" onClick={() => shiftCopyTarget(-7)}><ChevronLeft className="h-4 w-4" /></Button>
                <Input type="date" data-testid="pl-copy-target" value={copyTarget || ""} onChange={(e) => setCopyTargetDate(e.target.value)} />
                <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid="pl-copy-next" onClick={() => shiftCopyTarget(7)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
              {copyTarget && <p className="text-xs text-muted-foreground mt-1">{weekInfo(copyTarget, meta.lang).period}</p>}
            </div>
            {isReferentDementia && (
              <div data-testid="pl-copy-plan">
                <Label>{t("planning.copyPlanQuestion")}</Label>
                <div className="flex gap-2 mt-1">
                  <button type="button" data-testid="pl-copy-plan-commun" onClick={() => { setCopyPlan("commun"); setCopyPreview(null); }}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold border transition ${copyPlan === "commun" ? "bg-teal-500 text-white border-teal-500" : "bg-background hover:bg-muted"}`}>{t("planning.viewCommon")}</button>
                  <button type="button" data-testid="pl-copy-plan-referent" onClick={() => { setCopyPlan("referent"); setCopyPreview(null); }}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold border transition ${copyPlan === "referent" ? "bg-violet-600 text-white border-violet-600" : "bg-background hover:bg-muted"}`}>{t("planning.viewReferent")}</button>
                </div>
              </div>
            )}
            {canCopyAll && (
              <div>
                <Label>{t("planning.copyScope")}</Label>
                <select className="w-full h-10 rounded-md border px-3 bg-background" value={copyScope} data-testid="pl-copy-scope" onChange={(e) => { setCopyScope(e.target.value); setCopyPreview(null); }}>
                  <option value="mine">{t("planning.copyScopeMine")}</option>
                  <option value="all">{t("planning.copyScopeAll")}</option>
                </select>
                {copyScope === "all" && <p className="text-[11px] text-muted-foreground mt-1">{t("planning.copyScopeAllHint")}</p>}
              </div>
            )}

            {!copyPreview && (
              <Button className="w-full" data-testid="pl-copy-preview-btn" onClick={runCopyPreview} disabled={copyBusy || !copyTarget || copyTarget === meta.week_start}>
                {copyBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />} {t("planning.copyPreviewBtn")}
              </Button>
            )}

            {copyPreview && (
              <div className="rounded-lg border p-3 space-y-3" data-testid="pl-copy-result">
                <p className="text-sm">{t("planning.copyPreviewSummary", { count: copyPreview.source_count, source: weekInfo(meta.week_start, meta.lang).period, target: weekInfo(copyTarget, meta.lang).period })}</p>
                {copyPreview.source_count === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("planning.copyNothing")}</p>
                ) : copyPreview.target_total > 0 ? (
                  <div className="rounded-md border border-amber-400/50 bg-amber-500/10 p-3 space-y-2">
                    <p className="text-sm font-medium flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /> {t("planning.copyTargetHasActivities", { count: copyPreview.target_total })}</p>
                    <div className="flex flex-col gap-2">
                      <Button data-testid="pl-copy-mode-add" onClick={() => runCopy("add")} disabled={copyBusy}>{t("planning.copyModeAdd")}</Button>
                      <span className="text-[11px] text-muted-foreground -mt-1">{t("planning.copyModeAddHint")}</span>
                      <Button variant="outline" data-testid="pl-copy-mode-free" onClick={() => runCopy("free_only")} disabled={copyBusy || copyPreview.free_only_count === 0}>{t("planning.copyModeFree")}</Button>
                      <span className="text-[11px] text-muted-foreground -mt-1">{t("planning.copyModeFreeHint", { count: copyPreview.free_only_count, skipped: copyPreview.skipped_count })}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">{t("planning.copyTargetEmpty")}</p>
                    <Button className="w-full" data-testid="pl-copy-confirm" onClick={() => runCopy("add")} disabled={copyBusy}>
                      {copyBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Copy className="h-4 w-4 mr-1" />} {t("planning.copyConfirm")}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" data-testid="pl-copy-cancel" onClick={() => setCopyOpen(false)}>{t("planning.cancel")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ObjectivesEditor({ objectives, onChange, otypes = [], t }) {
  const setAt = (i, v) => onChange(objectives.map((o, idx) => (idx === i ? v : o)));
  const removeAt = (i) => onChange(objectives.filter((_, idx) => idx !== i));
  const addLine = () => onChange([...objectives, ""]);
  return (
    <div className="mt-4 rounded-lg border p-3 bg-teal-500/[0.04]" data-testid="pl-objectives">
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-teal-600">
          <Target className="h-4 w-4" />{t("planning.objectives.title")}
        </span>
        <button type="button" onClick={addLine} data-testid="pl-obj-add" className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600 hover:text-teal-500">
          <Plus className="h-3.5 w-3.5" />{t("planning.objectives.add")}
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2">{t("planning.objectives.hint")}</p>
      {objectives.length === 0 ? (
        <p className="text-xs text-muted-foreground italic" data-testid="pl-obj-empty">{t("planning.objectives.empty")}</p>
      ) : (
        <div className="space-y-2">
          {objectives.map((o, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`pl-obj-row-${i}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-teal-500 shrink-0" />
              <ObjectiveTypeField value={o} otypes={otypes} index={i} onChange={(v) => setAt(i, v)} placeholder={t("planning.objectives.ph")} />
              <button type="button" onClick={() => removeAt(i)} data-testid={`pl-obj-del-${i}`} className="text-muted-foreground hover:text-red-500 shrink-0">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const _normObj = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
// Combobox pré-rempli (41 objectifs types) pour un objectif d'activité — saisie libre possible.
function ObjectiveTypeField({ value, otypes, index, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const q = _normObj(value);
  const filtered = otypes.filter((o) => !q || _normObj(o.label).includes(q)).slice(0, 60);
  return (
    <div className="relative flex-1" ref={ref}>
      <Input value={value} onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        data-testid={`pl-obj-input-${index}`} className="h-9 text-sm w-full" placeholder={placeholder} autoComplete="off" />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border bg-background shadow-xl" data-testid={`pl-obj-typelist-${index}`}>
          {filtered.map((o) => (
            <button key={o.id} type="button" onClick={() => { onChange(o.label); setOpen(false); }}
              data-testid={`pl-obj-typeopt-${index}-${o.id}`} className="w-full text-left px-3 py-2 hover:bg-muted border-b last:border-0">
              <div className="text-sm">{o.label}</div>
              {o.domain && <div className="text-[11px] text-teal-600">{o.domain}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

